import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Text, size, tags, useCheckBreakpoint, canUseDOM } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';

import { useStore } from '../../modules/store/cobrandStore';
import { COBRANDSESSIONDATA, CREDITCARDENDPOINT } from '../../modules/constants';
import { isMobile } from '../../modules/utils';

import { CtaComponentTypes } from './CtaComponent.types';
import { StyledCompareButton } from './CtaComponent.styles';

export const CtaComponentConfig = {
  emptyLabel: 'Cta Component',
  isEmpty: () => true,
  resourceType: `/components/content/cta-component`,
};
export const CtaComp = (props: CtaComponentTypes) => {
  const {
    openInNewTab,
    linkText,
    linkUrl,
    trackingContentPosition,
    trackingDescription,
    trackingOfferType,
    trackingTag,
    merchandisingCategory,
    atCCeVar48,
    additionalTrackingVariables,
    impressionCount,
    clickTrack,
    impressionTrack,
    ctaDescriptionMobile,
    ctaDescriptionDesktop,
    enableButtonByDefault,
    targetExperience,
  } = props;

  const trackingProperties = {
    trackingContentPosition,
    trackingDescription,
    trackingOfferType,
    trackingTag,
    merchandisingCategory,
    atCCeVar48,
    additionalTrackingVariables,
    clickTrack: clickTrack === 'true',
    impressionTrack: impressionTrack === 'true',
    impressionCount: impressionCount === 'true',
    isCoBrand: true,
  };
  const linkType = openInNewTab ? 'external' : 'internal';
  const clickTrackValue = `${trackingProperties?.trackingContentPosition}|${trackingProperties?.trackingDescription}|${linkType}|${trackingProperties?.additionalTrackingVariables}`;
  const enableCTAButton = enableButtonByDefault === 'true';

  const { cobrandData, setCobrandData } = useStore(store => store);
  const [enableCardComparison, setEnableCardComparison] = useState<boolean>(false);
  const [compareButtonText, setCompareButtonText] = useState<string>('');
  const [compareButtonUrl, setCompareButtonUrl] = useState<string>('');

  const isViewportL = useCheckBreakpoint('viewportL');
  const isMobileDevice: boolean = isMobile();
  const router = useRouter();

  const buildUrlParams = useCallback(
    (cardParams: string) => {
      const params = new URLSearchParams();

      const cardParamsArray = cardParams.split('&').filter(Boolean);
      cardParamsArray.forEach(param => {
        const [key, value] = param.split('=');
        if (key && value) {
          params.set(key, value);
        }
      });

      if (targetExperience) {
        params.set('experience', targetExperience);
      }

      if (router?.query) {
        for (const [key, value] of Object.entries(router.query)) {
          params.set(key, value as string);
        }
      }
      return `?${params.toString()}`;
    },
    [router.query, targetExperience]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cobrandDataFromSessionStorage = sessionStorage?.getItem(COBRANDSESSIONDATA);
      const creditCardEndPointFromSessionStorage = sessionStorage?.getItem(CREDITCARDENDPOINT);
      if (cobrandDataFromSessionStorage) {
        setCobrandData(JSON.parse(cobrandDataFromSessionStorage));
        sessionStorage.removeItem(COBRANDSESSIONDATA);
      }
      if (creditCardEndPointFromSessionStorage) {
        sessionStorage.removeItem(CREDITCARDENDPOINT);
      }
    }
  }, [cobrandData, setCobrandData]);

  useEffect(() => {
    if (cobrandData?.allCarouselCards?.length) {
      const cardParams = cobrandData.allCarouselCards.map((element, i) => `cardtype${i + 1}=${element}`).join('&');

      const finalUrl = buildUrlParams(cardParams);
      setCompareButtonUrl(finalUrl);
    }
  }, [cobrandData, buildUrlParams]);

  const getCardCompareUrl = useCallback(() => {
    const cardParams = cobrandData.selectedCards
      .filter(card => card.checked)
      .map((element, i) => `cardtype${i + 1}=${element.cardname}`)
      .join('&');

    return buildUrlParams(cardParams);
  }, [cobrandData.selectedCards, buildUrlParams]);

  useEffect(() => {
    const selectedCheckboxes = cobrandData?.selectedCards?.filter(card => card?.checked);
    setEnableCardComparison(selectedCheckboxes.length >= 2 ? true : false);
    const checkedCheckboxes = cobrandData.selectedCards.filter(({ checked }) => checked);
    setCompareButtonText(checkedCheckboxes.length > 0 ? ` (${checkedCheckboxes.length})` : '');
  }, [cobrandData]);

  const setSelections = () => {
    if (canUseDOM) {
      sessionStorage?.setItem(COBRANDSESSIONDATA, JSON.stringify(cobrandData));
      sessionStorage?.setItem(CREDITCARDENDPOINT, JSON.stringify(router?.asPath));
    }
  };
  return (
    <StyledCompareButton data-testid="m-cta-component" data-component-name="m-cobrand-cta-component">
      <div className="col-12 d-flex justify-content-center my-3">
        <div className="text-center">
          {ctaDescriptionDesktop && (
            <Text
              fontSize={size.medium}
              element={tags.paragraph}
              copyText={isViewportL && !isMobileDevice ? ctaDescriptionDesktop : ctaDescriptionMobile}
              customClass={'t-subtitle-m  mb-3'}
            />
          )}
          {linkText && enableCTAButton && (
            <Button
              className={clsx('m-button-l m-button-secondary compare-button mx-auto px-4')}
              isLink={true}
              href={linkUrl + compareButtonUrl}
              buttonCopy={linkText}
              target={openInNewTab === 'true' ? '_blank' : '_self'}
              linkType={linkType}
              trackingProperties={trackingProperties}
              callback={setSelections}
              testId="comparebtn"
              custom_click_track_value={clickTrackValue}
            />
          )}
          {linkText && !enableCTAButton && (
            <Button
              className={clsx(
                'm-button-l m-button-secondary compare-button mx-auto px-4',
                !enableCardComparison && 'disabled'
              )}
              isLink={true}
              href={linkUrl + getCardCompareUrl()}
              buttonCopy={linkText + compareButtonText}
              target={openInNewTab === 'true' ? '_blank' : '_self'}
              linkType={linkType}
              isDisabled={enableCardComparison}
              trackingProperties={{
                ...trackingProperties,
              }}
              callback={setSelections}
              testId="comparebtn2"
              custom_click_track_value={clickTrackValue}
            />
          )}
        </div>
      </div>
    </StyledCompareButton>
  );
};

export const CtaComponent = (props: CtaComponentTypes) => {
  return (
    <EditableComponent config={CtaComponentConfig} {...props}>
      <CtaComp {...props} />
    </EditableComponent>
  );
};
